import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { ThemeProvider, useThemeContext } from '@/contexts/ThemeContext';
import { Toaster } from '@/components/ui/sonner';
import AuthCallback from '@/pages/AuthCallback';
import LandingPage from '@/pages/LandingPage';
import DriversLanding from '@/pages/DriversLanding';
import ShippersLanding from '@/pages/ShippersLanding';
import FleetsLanding from '@/pages/FleetsLanding';
import LoginPage from '@/pages/LoginPage';
import RegisterPage from '@/pages/RegisterPage';
import ChangePasswordPage from '@/pages/ChangePasswordPage';
import AdminLayout from '@/pages/admin/AdminLayout';
import AdminDashboard from '@/pages/admin/AdminDashboard';
import AdminLoads from '@/pages/admin/AdminLoads';
import AdminDrivers from '@/pages/admin/AdminDrivers';
import AdminCustomers from '@/pages/admin/AdminCustomers';
import AdminAnalytics from '@/pages/admin/AdminAnalytics';
import AdminMessages from '@/pages/admin/AdminMessages';
import AdminEquipment from '@/pages/admin/AdminEquipment';
import AdminTickets from '@/pages/admin/AdminTickets';
import AdminTraining from '@/pages/admin/AdminTraining';
import AdminDispatch from '@/pages/admin/AdminDispatch';
import AdminAccounting from '@/pages/admin/AdminAccounting';
import AdminSettings from '@/pages/admin/AdminSettings';
import AdminDriverDetail from '@/pages/admin/AdminDriverDetail';
import AdminCustomerDetail from '@/pages/admin/AdminCustomerDetail';
import AdminClientVerification from '@/pages/admin/AdminClientVerification';
import AdminEquipmentDetail from '@/pages/admin/AdminEquipmentDetail';
import AdminFleetMap from '@/pages/admin/AdminFleetMap';
import AdminLoadDetail from '@/pages/admin/AdminLoadDetail';
import AdminDriverOnboarding from '@/pages/admin/AdminDriverOnboarding';
import DriverLayout from '@/pages/driver/DriverLayout';
import DriverDashboard from '@/pages/driver/DriverDashboard';
import DriverHistory from '@/pages/driver/DriverHistory';
import DriverProfile from '@/pages/driver/DriverProfile';
import DriverPayments from '@/pages/driver/DriverPayments';
import ClientLayout from '@/pages/client/ClientLayout';
import ClientDashboard from '@/pages/client/ClientDashboard';
import ClientProfile from '@/pages/client/ClientProfile';
import ClientOnboarding from '@/pages/client/ClientOnboarding';

function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    const dest = user.role === 'admin' ? '/admin' : user.role === 'driver' ? '/driver' : '/client';
    return <Navigate to={dest} replace />;
  }
  return children;
}

function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/for-drivers" element={<DriversLanding />} />
      <Route path="/for-shippers" element={<ShippersLanding />} />
      <Route path="/for-fleets" element={<FleetsLanding />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/change-password" element={<ChangePasswordPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/dashboard" element={<DashboardRedirect />} />
      <Route path="/admin/fleet-map" element={<ProtectedRoute allowedRoles={['admin']}><AdminFleetMap /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute allowedRoles={['admin']}><AdminLayout /></ProtectedRoute>}>
        <Route index element={<AdminDashboard />} />
        <Route path="loads" element={<AdminLoads />} />
        <Route path="loads/:loadId" element={<AdminLoadDetail />} />
        <Route path="drivers" element={<AdminDrivers />} />
        <Route path="drivers/:driverId" element={<AdminDriverDetail />} />
        <Route path="drivers/:driverId/onboarding" element={<AdminDriverOnboarding />} />
        <Route path="customers" element={<AdminCustomers />} />
        <Route path="customers/:customerId" element={<AdminCustomerDetail />} />
        <Route path="customers/:customerId/verify" element={<AdminClientVerification />} />
        <Route path="analytics" element={<AdminAnalytics />} />
        <Route path="dispatch" element={<AdminDispatch />} />
        <Route path="equipment" element={<AdminEquipment />} />
        <Route path="equipment/:equipmentId" element={<AdminEquipmentDetail />} />
        <Route path="tickets" element={<AdminTickets />} />
        <Route path="training" element={<AdminTraining />} />
        <Route path="accounting" element={<AdminAccounting />} />
        <Route path="messages" element={<AdminMessages />} />
        <Route path="settings" element={<AdminSettings />} />
      </Route>
      <Route path="/driver" element={<ProtectedRoute allowedRoles={['driver']}><DriverLayout /></ProtectedRoute>}>
        <Route index element={<DriverDashboard />} />
        <Route path="history" element={<DriverHistory />} />
        <Route path="payments" element={<DriverPayments />} />
        <Route path="profile" element={<DriverProfile />} />
      </Route>
      <Route path="/client/onboarding" element={<ProtectedRoute allowedRoles={['client']}><ClientOnboarding /></ProtectedRoute>} />
      <Route path="/client" element={<ProtectedRoute allowedRoles={['client']}><ClientLayout /></ProtectedRoute>}>
        <Route index element={<ClientDashboard />} />
        <Route path="shipments" element={<ClientDashboard />} />
        <Route path="new-shipment" element={<ClientDashboard />} />
        <Route path="history" element={<ClientDashboard />} />
        <Route path="profile" element={<ClientProfile />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function DashboardRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  const dest = user.role === 'admin' ? '/admin' : user.role === 'driver' ? '/driver' : '/client';
  return <Navigate to={dest} replace />;
}

function ThemedToaster() {
  const { theme } = useThemeContext();
  return <Toaster position="top-right" theme={theme} />;
}

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <AppRouter />
          <ThemedToaster />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}
